import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID } from './slideLevelClassificationModel';
import styles from './HybridClassSelect.module.css';

const TRIGGER_TRUNCATE = 52;
const ROW_PREVIEW_TRUNCATE = 40;
const MENU_GAP_PX = 2;
const MENU_MAX_HEIGHT_PX = 256;
const MENU_MIN_FLIP_SPACE_PX = 120;
const MENU_Z_INDEX = 1300;

/** @param {string} s @param {number} max */
export function truncateWithEllipsis(s, max) {
    const t = String(s ?? '');
    if (t.length <= max) return t;
    if (max <= 1) return '…';
    return `${t.slice(0, max - 1)}…`;
}

/**
 * Short label for "Other (free text)" style config: use text before "(" so the UI can show
 * "Other: …" instead of repeating the full parenthetical when an answer exists.
 * @param {string} optionLabel
 */
export function compactFreeTextOptionHeadline(optionLabel) {
    const s = String(optionLabel ?? '').trim();
    if (!s) return 'Other';
    const i = s.indexOf('(');
    if (i > 0) return s.slice(0, i).trimEnd();
    return s;
}

/**
 * @param {HTMLElement} triggerEl
 */
function computeFloatingMenuStyle(triggerEl) {
    const rect = triggerEl.getBoundingClientRect();
    const maxWidth = Math.min(
        22 * 16,
        Math.max(rect.width, 10 * 16),
        window.innerWidth - 32,
    );
    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP_PX - 8;
    const spaceAbove = rect.top - MENU_GAP_PX - 8;
    const openAbove = spaceBelow < MENU_MIN_FLIP_SPACE_PX && spaceAbove > spaceBelow;
    const available = openAbove ? spaceAbove : spaceBelow;
    const maxHeight = Math.min(
        MENU_MAX_HEIGHT_PX,
        window.innerHeight * 0.5,
        Math.max(80, available),
    );

    if (openAbove) {
        return {
            position: 'fixed',
            left: rect.left,
            bottom: window.innerHeight - rect.top + MENU_GAP_PX,
            minWidth: rect.width,
            maxWidth,
            maxHeight,
            zIndex: MENU_Z_INDEX,
        };
    }

    return {
        position: 'fixed',
        top: rect.bottom + MENU_GAP_PX,
        left: rect.left,
        minWidth: rect.width,
        maxWidth,
        maxHeight,
        zIndex: MENU_Z_INDEX,
    };
}

/**
 * Custom class picker for hybrid (discrete + free text). Discrete-only mode keeps native <select>
 * in TopBarControls as a reference implementation.
 */
export function HybridClassSelect({
    categories,
    value,
    categoryId,
    freeTextAnswer,
    freeTextOptionLabel,
    disabled,
    ariaLabelledBy,
    onCommitSelection,
}) {
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState(null);
    const triggerRef = useRef(null);
    const listId = useId();

    useLayoutEffect(() => {
        if (!open || !triggerRef.current) {
            setMenuStyle(null);
            return undefined;
        }
        const update = () => {
            if (!triggerRef.current) return;
            setMenuStyle(computeFloatingMenuStyle(triggerRef.current));
        };
        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        function onDocMouseDown(e) {
            const t = e.target;
            if (triggerRef.current?.contains(t)) return;
            if (t.closest?.('[data-slc-hybrid-select-menu]')) return;
            setOpen(false);
        }
        function onKeyDown(e) {
            if (e.key === 'Escape') {
                e.preventDefault();
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', onDocMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    const triggerText = (() => {
        const v = String(value);
        if (!v) return '— Select —';
        if (v === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID) {
            const raw = String(freeTextAnswer ?? '').trim();
            if (!raw) return freeTextOptionLabel;
            const head = compactFreeTextOptionHeadline(freeTextOptionLabel);
            return `${head}: ${truncateWithEllipsis(raw, TRIGGER_TRUNCATE)}`;
        }
        const cat = categories.find((c) => String(c.id) === v);
        return cat ? (cat.label?.trim() ? cat.label : String(cat.id)) : '— Select —';
    })();

    const triggerTitle =
        String(value) === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID
        && String(freeTextAnswer ?? '').trim()
            ? String(freeTextAnswer)
            : undefined;

    function pick(v) {
        onCommitSelection(v);
        setOpen(false);
    }

    const freeSelected = String(categoryId) === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID;
    const freePreviewRaw = String(freeTextAnswer ?? '').trim();
    const freePreview =
        freePreviewRaw.length > 0
            ? truncateWithEllipsis(freePreviewRaw, ROW_PREVIEW_TRUNCATE)
            : null;
    const freeRowHeadline = compactFreeTextOptionHeadline(freeTextOptionLabel);
    const freeTextAriaLabel = freePreviewRaw
        ? `${freeTextOptionLabel}. Current answer: ${truncateWithEllipsis(freePreviewRaw, 72)}. Opens text editor.`
        : `${freeTextOptionLabel}. Opens text editor.`;

    const menuNode = open && !disabled && menuStyle ? (
        <ul
            id={listId}
            data-slc-hybrid-select-menu
            className={styles.panelFloating}
            style={menuStyle}
            role="listbox"
            aria-labelledby={ariaLabelledBy}
        >
            <li role="presentation">
                <button
                    type="button"
                    role="option"
                    aria-selected={value === ''}
                    className={`${styles.option} ${value === '' ? styles.optionSelected : ''}`}
                    onClick={() => pick('')}
                >
                    — Select —
                </button>
            </li>
            {categories.map((c) => {
                const id = String(c.id);
                const label = c.label?.trim() ? c.label : id;
                const selected = value === id;
                return (
                    <li key={c.id} role="presentation">
                        <button
                            type="button"
                            role="option"
                            aria-selected={selected}
                            className={`${styles.option} ${selected ? styles.optionSelected : ''}`}
                            onClick={() => pick(id)}
                        >
                            {label}
                        </button>
                    </li>
                );
            })}
            <li role="presentation">
                <button
                    type="button"
                    className={`${styles.option} ${styles.freeTextOption} ${freeSelected ? styles.optionSelected : ''}`}
                    role="option"
                    aria-selected={freeSelected}
                    aria-label={freeTextAriaLabel}
                    onClick={() => pick(SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID)}
                >
                    {freePreview ? (
                        <span>{`${freeRowHeadline}: ${freePreview}`}</span>
                    ) : (
                        <span>{freeTextOptionLabel}</span>
                    )}
                </button>
            </li>
        </ul>
    ) : null;

    return (
        <div className={styles.wrap}>
            <button
                ref={triggerRef}
                type="button"
                className={styles.trigger}
                disabled={disabled}
                aria-labelledby={ariaLabelledBy}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={open ? listId : undefined}
                title={triggerTitle}
                onClick={() => {
                    if (!disabled) setOpen((o) => !o);
                }}
            >
                <span className={styles.triggerLabel}>{triggerText}</span>
                <span className={styles.chevron} aria-hidden>
                    ▾
                </span>
            </button>
            {menuNode ? createPortal(menuNode, document.body) : null}
        </div>
    );
}
